import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, Heart } from "lucide-react";
import { api } from "@/lib/api";

const TINTS = ["#FFE8D6", "#E3F2E8", "#E5EEFB", "#FBE7F0", "#FFF3C4"];

export function VerseGifts({ kidId }: { kidId: string }) {
  const [gifts, setGifts] = useState<any[] | null>(null);
  useEffect(() => {
    api.get(`/kids/${kidId}/verse-gifts`).then((r) => {
      setGifts(r.data);
      if (r.data.some((g: any) => !g.seen)) api.post(`/kids/${kidId}/verse-gifts/seen`).catch(() => {});
    }).catch(() => setGifts([]));
  }, [kidId]);
  if (!gifts?.length) return null;
  return (
    <section className="mt-10 rounded-[2rem] bg-gradient-to-br from-[#FBE7F0] to-white p-6 sm:p-8" data-testid="verse-gifts">
      <h2 className="flex items-center gap-2 text-2xl font-semibold text-slate-800"><Gift className="h-6 w-6 text-[#E07A5F]" />Verses from my family</h2>
      <p className="mb-6 text-slate-500">Special verses someone who loves you picked just for you.</p>
      <div className="grid gap-5 sm:grid-cols-2">
        {gifts.map((g, i) => (
          <motion.div key={g.id} initial={{ opacity: 0, scale: 0.9, rotate: i % 2 ? 2 : -2 }} animate={{ opacity: 1, scale: 1, rotate: i % 2 ? 1.5 : -1.5 }} transition={{ delay: i * 0.08, type: "spring" }}
            className="relative rounded-3xl border-4 border-white p-6 shadow-lg" style={{ background: TINTS[i % TINTS.length] }} data-testid={`verse-gift-card-${g.id}`}>
            {!g.seen && <span className="absolute -right-2 -top-3 rotate-6 rounded-full bg-[#E07A5F] px-3 py-1 text-sm font-bold text-white shadow" data-testid={`verse-gift-new-${g.id}`}>New!</span>}
            <p className="text-xl font-semibold leading-snug text-slate-800">“{g.text}”</p>
            <p className="mt-2 font-semibold text-[#E07A5F]">{g.ref}</p>
            {g.message && <p className="mt-4 flex items-start gap-2 rounded-2xl bg-white/80 p-3 text-slate-700" data-testid={`verse-gift-message-${g.id}`}><Heart className="mt-1 h-4 w-4 shrink-0 fill-rose-400 text-rose-400" />{g.message}</p>}
            <p className="mt-3 text-right text-sm font-semibold text-slate-500">from {g.from_name}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

export function VerseGiftBanner({ kidId }: { kidId: string }) {
  const [n, setN] = useState(0);
  useEffect(() => { api.get(`/kids/${kidId}/verse-gifts`).then((r) => setN(r.data.filter((g: any) => !g.seen).length)).catch(() => {}); }, [kidId]);
  if (!n) return null;
  return (
    <Link to="/kids/stickers" data-testid="verse-gift-banner" className="flex items-center gap-4 rounded-[2rem] bg-[#FBE7F0] p-6 shadow ring-2 ring-rose-200 transition-transform hover:scale-[1.01]">
      <motion.span animate={{ rotate: [0, -10, 10, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E07A5F] text-white"><Gift className="h-7 w-7" /></motion.span>
      <span><span className="block text-2xl font-semibold text-slate-800">You got {n === 1 ? "a verse" : `${n} verses`} from your family!</span><span className="text-lg text-slate-600">Open your sticker book to see it</span></span>
    </Link>
  );
}
